import { getItem, setItemOrThrow } from '@ms/yammer-libs-local-storage';
import type { EcsPersistKey } from '@ms/yammer-libs-static-config';

import {
  LegacyEcsPersistenceError,
  parseLegacyEcsPersistedState,
  serializeLegacyEcsPersistedState,
} from './legacyPersistedState';
import type { EcsSourceSnapshot } from './store';

export {
  LegacyEcsPersistenceError,
  parseLegacyEcsPersistedState,
  serializeLegacyEcsPersistedState,
  type LegacyEcsPersistedFields,
  type LegacyEcsPersistenceErrorReason,
} from './legacyPersistedState';

export type LegacyEcsPersistedStateReadResult =
  | {
      readonly status: 'success';
      readonly snapshot: EcsSourceSnapshot;
    }
  | {
      readonly status: 'absent';
    }
  | {
      readonly status: 'failed';
      readonly error: LegacyEcsPersistenceError;
    };

export const readLegacyEcsPersistedState = (persistKey: EcsPersistKey): LegacyEcsPersistedStateReadResult => {
  try {
    const persistedState = getItem<unknown>(`persist:${persistKey}`);

    return persistedState == null
      ? { status: 'absent' }
      : {
          status: 'success',
          snapshot: parseLegacyEcsPersistedState(persistedState),
        };
  } catch (error) {
    return {
      status: 'failed',
      error: error instanceof LegacyEcsPersistenceError ? error : new LegacyEcsPersistenceError('read-failed'),
    };
  }
};

export const doesLegacyEcsPersistedStateExist = (persistKey: EcsPersistKey): boolean =>
  readLegacyEcsPersistedState(persistKey).status === 'success';

const isPersistedStateRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  typeof value === 'object' && value != null && !Array.isArray(value);

const readLegacyEcsPersistedStateForWrite = (storageKey: string): unknown => {
  try {
    return getItem<unknown>(storageKey);
  } catch (error) {
    if (error instanceof SyntaxError) {
      return null;
    }

    throw error;
  }
};

export const writeLegacyEcsPersistedState = (persistKey: EcsPersistKey, snapshot: EcsSourceSnapshot): void => {
  const storageKey = `persist:${persistKey}`;
  const currentPersistedState = readLegacyEcsPersistedStateForWrite(storageKey);

  setItemOrThrow(storageKey, {
    ...(isPersistedStateRecord(currentPersistedState) ? currentPersistedState : {}),
    ...serializeLegacyEcsPersistedState(snapshot),
  });
};
